import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useState, type ReactNode } from 'react';
import { Button } from 'react-aria-components';
import { IconButton } from '../atoms/Button';
import { cn } from '../lib/cn';

const at = (d: string) => new Date(`${d}T12:00:00Z`);
const iso = (d: Date) => d.toISOString().slice(0, 10);

export interface MonthPickerProps {
  /** The picked day (YYYY-MM-DD). */
  value: string;
  /** A range ends here (all-day over several days). */
  end?: string;
  today: string;
  onPick: (day: string) => void;
  /** Mo … So, in the device language. */
  weekdays: string[];
  monthLabel: (firstOfMonth: string) => string;
  dayLabel: (day: string) => string;
  prevLabel: string;
  nextLabel: string;
  /** "November" – for jumping months ahead (tap the title) */
  monthName: (firstOfMonth: string) => string;
  /** "Monat wählen" */
  pickMonthLabel: string;
  /** a small mark under a day, e.g. the shift of a plan */
  mark?: (day: string) => ReactNode;
}

/**
 * A month to tap a day in (1.25, Mike 20.10.: "die wenigsten Termine sind heute oder morgen,
 * sondern irgendwann in der Zukunft") – one tap for any day, arrows for later months.
 */
export function MonthPicker({
  value,
  end,
  today,
  onPick,
  weekdays,
  monthLabel,
  dayLabel,
  prevLabel,
  nextLabel,
  monthName,
  pickMonthLabel,
  mark,
}: MonthPickerProps) {
  // tap the title for the twelve months – a day a year ahead in three taps (Mike, 21.10.)
  const [months, setMonths] = useState(false);
  const [first, setFirst] = useState(`${value.slice(0, 7)}-01`);
  const f = at(first);
  const lead = (f.getUTCDay() + 6) % 7;
  const count = new Date(Date.UTC(f.getUTCFullYear(), f.getUTCMonth() + 1, 0)).getUTCDate();
  const days = Array.from(
    { length: count },
    (_, i) => `${first.slice(0, 8)}${String(i + 1).padStart(2, '0')}`,
  );
  const step = (n: number) =>
    setFirst(iso(new Date(Date.UTC(f.getUTCFullYear(), f.getUTCMonth() + n, 1, 12))));
  const last = end && end > value ? end : value;
  // swipe sideways for the next or previous month, like any calendar (Mike, 20.10.)
  const [from, setFrom] = useState<{ x: number; y: number }>();
  const year = f.getUTCFullYear();
  if (months)
    return (
      <div className="flex flex-col gap-gap-xs">
        <div className="flex items-center justify-between">
          <IconButton icon={CaretLeft} aria-label={prevLabel} onPress={() => step(-12)} />
          <span aria-live="polite" className="font-display text-lg font-black tabular-nums">
            {year}
          </span>
          <IconButton icon={CaretRight} aria-label={nextLabel} onPress={() => step(12)} />
        </div>
        <div className="grid grid-cols-3 gap-gap-xs">
          {Array.from({ length: 12 }, (_, i) => {
            const m = `${year}-${String(i + 1).padStart(2, '0')}-01`;
            return (
              <Button
                key={m}
                aria-pressed={m === first}
                onPress={() => {
                  setFirst(m);
                  setMonths(false);
                }}
                className={cn(
                  'min-h-touch-min cursor-pointer rounded-control font-semibold outline-none',
                  'data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-pressed:scale-95',
                  m === first
                    ? 'bg-action-primary text-text-on-action'
                    : 'bg-surface-raised text-text-primary',
                )}
              >
                {monthName(m)}
              </Button>
            );
          })}
        </div>
      </div>
    );
  return (
    <div className="flex flex-col gap-gap-xs">
      <div className="flex items-center justify-between">
        <IconButton icon={CaretLeft} aria-label={prevLabel} onPress={() => step(-1)} />
        <Button
          aria-label={`${monthLabel(first)} – ${pickMonthLabel}`}
          onPress={() => setMonths(true)}
          className="min-h-touch-min cursor-pointer rounded-control px-inset-sm font-display text-lg font-black underline decoration-dotted underline-offset-4 outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus"
        >
          {monthLabel(first)}
        </Button>
        <IconButton icon={CaretRight} aria-label={nextLabel} onPress={() => step(1)} />
      </div>
      <div
        className="grid touch-pan-y grid-cols-7 gap-0.5 text-center"
        onPointerDownCapture={(e) => setFrom({ x: e.clientX, y: e.clientY })}
        onPointerUpCapture={(e) => {
          if (!from) return;
          const dx = e.clientX - from.x;
          setFrom(undefined);
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(e.clientY - from.y))
            step(dx < 0 ? 1 : -1);
        }}
      >
        {weekdays.map((w) => (
          <span key={w} aria-hidden className="text-xs font-bold text-text-secondary">
            {w}
          </span>
        ))}
        {Array.from({ length: lead }, (_, i) => (
          <span key={`x${i}`} />
        ))}
        {days.map((d) => {
          const edge = d === value || d === last;
          const inside = d > value && d < last;
          return (
            <Button
              key={d}
              aria-label={dayLabel(d)}
              aria-pressed={edge}
              onPress={() => onPick(d)}
              className={cn(
                'flex min-h-touch-min cursor-pointer flex-col items-center justify-center rounded-control font-semibold tabular-nums outline-none',
                'data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-pressed:scale-95',
                edge
                  ? 'bg-action-primary text-text-on-action'
                  : inside
                    ? 'bg-accent-secondary-soft text-text-primary'
                    : d < today
                      ? 'text-text-muted'
                      : 'text-text-primary data-hovered:bg-surface-raised',
                d === today && !edge && 'border-2 border-border-default',
              )}
            >
              {Number(d.slice(8))}
              {mark?.(d)}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
